declare const CustomerIdBrand: unique symbol;

/**
 * Customer info with format: {country_code}_{id}
 */
export type CustomerId = string & { readonly [CustomerIdBrand]: "CustomerId" };

export class CustomerIdError extends Error {
  constructor(
    public readonly kind: "Format" | "InvalidCountryCode" | "InvalidId",
    message: string,
  ) {
    super(message);
  }
}

const COUNTRY_CODE_PATTERN = /^[A-Z]{2}$/;
const ID_PATTERN = /^[0-9]+$/;
const FULL_PATTERN = /^([A-Z]{2})_([0-9]+)$/;

const fromParts = (id: string, countryCode: string): CustomerId => {
  if (!COUNTRY_CODE_PATTERN.test(countryCode)) {
    throw new CustomerIdError("InvalidCountryCode", `Invalid country code "${countryCode}"`);
  }
  if (!ID_PATTERN.test(id)) {
    throw new CustomerIdError("InvalidId", `Invalid customer info ID "${id}"`);
  }

  return `${countryCode}_${id}` as CustomerId;
};

const fromString = (str: string): CustomerId => {
  const match = FULL_PATTERN.exec(str);

  if (!match) {
    throw new CustomerIdError(
      "Format",
      `String "${str}" is not a valid CustomerId. Expected format: {country_code}_{id}`,
    );
  }

  return str as CustomerId;
};

const toParts = (id: CustomerId): { id: string; countryCode: string } => {
  const str = id as string;
  const match = FULL_PATTERN.exec(str);

  if (match && match[1] && match[2]) {
    return {
      id: match[2],
      countryCode: match[1],
    };
  } else {
    throw new CustomerIdError("Format", `CustomerId "${str}" has invalid format.`);
  }
};

const toString = (id: CustomerId): string => {
  return id as string;
};

export const CustomerId = {
  fromParts,
  fromString,
  toString,
  toParts,
} as const;
